import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { LineChart } from '@mui/x-charts';
import { AthleteTeamProfile, athleteDisplayName } from '@strength/domain';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { getLocale } from '../../../utils/i18n';
import { CoreExercise } from '../utils';

export const AthleteExerciseBenchmarkHistory = ({
  athlete,
  coreExercises,
  benchmarkGridId,
  open,
  onOpenChange,
}: {
  athlete: AthleteTeamProfile;
  coreExercises: CoreExercise[];
  benchmarkGridId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [selectedLibraryExerciseId, setSelectedLibraryExerciseId] = useState<
    string | null
  >(() => coreExercises[0]?.id ?? null);

  const athleteExerciseBenchmarksResult =
    trpc.athleteBenchmarks.fetchAthleteExerciseBenchmarks.useQuery(
      { profileId: athlete.id, benchmarkGridId },
      {
        staleTime: 10_000,
      },
    );

  const benchmarkHistoryResult =
    trpc.athleteBenchmarks.fetchBenchmarkHistoryForExercise.useQuery(
      {
        profileId: athlete.id,
        libraryExerciseId: selectedLibraryExerciseId ?? '',
        benchmarkGridId,
      },
      {
        staleTime: 10_000,
        enabled: !!selectedLibraryExerciseId,
      },
    );

  const benchmarkForExercise = useMemo(
    () =>
      athleteExerciseBenchmarksResult.data?.find(
        (benchmark) =>
          benchmark.libraryExerciseId === selectedLibraryExerciseId,
      ),
    [athleteExerciseBenchmarksResult.data, selectedLibraryExerciseId],
  );
  const benchmarks = benchmarkHistoryResult?.data ?? [];

  const benchmarkDates = benchmarks.map((benchmark) => benchmark.date);
  const minBenchmarkDate = getMinDateForGraph(benchmarkDates);
  const maxBenchmarkDate = getMaxDateForGraph(benchmarkDates);

  return (
    <Drawer open={open} onClose={() => onOpenChange(false)}>
      <DrawerHeader onClose={() => onOpenChange(false)}>
        <Typography variant="h2">{athleteDisplayName(athlete)}</Typography>
        <Select
          fullWidth
          value={selectedLibraryExerciseId}
          className="my-4"
          inputProps={{ 'aria-label': t('selectExercise') }}
          onChange={(e) => {
            setSelectedLibraryExerciseId(e.target.value);
          }}
        >
          {coreExercises.map((e) => {
            return (
              <MenuItem key={e.id} value={e.id}>
                {e.name}
              </MenuItem>
            );
          })}
        </Select>
      </DrawerHeader>
      <div className="px-8 grow">
        <div className="gap-0">
          {benchmarkForExercise ? (
            <>
              <Typography variant="h4">
                {benchmarkForExercise.value} {benchmarkForExercise.unit}
              </Typography>
              <Typography variant="body2Medium" color="text.secondary">
                {t('current')}
              </Typography>
            </>
          ) : (
            <Typography variant="body2Medium" color="text.secondary">
              {t('noCurrentMax')}
            </Typography>
          )}
        </div>

        <Box sx={{ height: 200, width: '100%' }}>
          <LineChart
            sx={{
              '& .MuiAreaElement-series-data': {
                fill: "url('#graph-gradient')",
              },
            }}
            xAxis={[
              {
                tickLabelStyle: {
                  display: 'none',
                },
                scaleType: 'time',
                data: benchmarks.map((benchmark) => new Date(benchmark.date)),
                min: minBenchmarkDate,
                max: maxBenchmarkDate,
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: {
                  display: 'none',
                },
              },
            ]}
            series={[
              {
                id: 'data',
                curve: 'linear',
                color: 'red',
                data: benchmarks.map((benchmark) => benchmark.weight),
                area: true,
                showMark: benchmarks.length === 1,
              },
            ]}
          >
            <defs>
              <linearGradient id="graph-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="red" stopOpacity={0.4} />
                <stop offset="45%" stopColor={red[300]} stopOpacity={0.2} />
                <stop offset="100%" stopColor="white" stopOpacity={0.1} />
              </linearGradient>
            </defs>
          </LineChart>
        </Box>

        <div className="space-y-2">
          {benchmarks.map((benchmark, i) => (
            <div key={i} className="grid grid-cols-2">
              <div className="flex flex-col">
                <Typography variant="body1" fontWeight="bold">
                  {benchmark.weight} {benchmark.unit}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  {DateTime.fromISO(benchmark.date).toLocaleString(
                    DateTime.DATE_SHORT,
                    { locale: getLocale() },
                  )}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>

      <DrawerFooter className="p-8">
        <Button variant="outlined" onClick={() => onOpenChange(false)}>
          {t('close')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

const getMinDateForGraph = (benchmarkDates: string[]) => {
  if (benchmarkDates.length === 0 || !benchmarkDates[0]) {
    return undefined;
  }

  const min = benchmarkDates[0];
  return DateTime.fromISO(min).minus({ days: 1 }).toJSDate();
};

const getMaxDateForGraph = (benchmarkDates: string[]) => {
  if (
    benchmarkDates.length === 0 ||
    !benchmarkDates[0] ||
    !benchmarkDates[benchmarkDates.length - 1]
  ) {
    return undefined;
  }

  if (benchmarkDates.length === 1) {
    return DateTime.fromISO(benchmarkDates[0]).plus({ days: 1 }).toJSDate();
  }

  const max = benchmarkDates[benchmarkDates.length - 1];
  if (!max) {
    return undefined;
  }
  return DateTime.fromISO(max).plus({ days: 1 }).toJSDate();
};
